<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AppNoticeModal from '~/components/app/AppNoticeModal.vue'
import { isAutomaticModalVisible } from '~/onboarding/automaticModalGate'
import { useOnboardingStore } from '~~/stores/onboarding'
import { useOrgAccessStatus, type OrgAccessStatusResponse } from '~/composables/ui/useOrgAccessStatus'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'
import { useDevOidcProfilesOverride } from '~/composables/auth/useDevOidcProfilesOverride'
import { useEffectiveGardianRoles } from '~/composables/auth/useEffectiveGardianRoles'
import { useMe } from '~/composables/auth/useMe'
import {
  contactMailtoAddress,
  contactPrimaryLine,
  contactShowNniSecondary,
  groupDirectionAdminsByNni,
  type ResolvedPerson,
} from '~~/utils/orgAccessContactDisplay'
import { ERole, derivePrimaryHabilitation } from '~~/utils/auth/e_roles'
import { isDeveloper, isLegacyAdmin } from '~~/utils/auth/abilities'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'

const SourceCheckbox = defineAsyncWithSkeleton(
  function loadSourceCheckbox() {
    return import('~/components/ui/SourceCheckbox.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const DIRECTORY_RESOLVE_CHUNK = 25

const STORAGE_MANAGER = 'monorga.dismiss-org-orientation-manager'
const STORAGE_ADMIN = 'monorga.dismiss-org-orientation-admin'

const { orgAccessStatus, refreshOrgAccessStatus } = useOrgAccessStatus()
const { roles: effectiveRoles } = useEffectiveGardianRoles()
const { refreshMe } = useMe()
const { overrideState } = useDevOidcProfilesOverride()
const { loggedIn, user } = useOidcAuth()

const onboardingStore = useOnboardingStore()

const showManagerModal = ref(false)
const showAdminModal = ref(false)
const showManagerModalToUser = computed(() =>
  isAutomaticModalVisible(showManagerModal.value, onboardingStore.isTourActive),
)
const showAdminModalToUser = computed(() =>
  isAutomaticModalVisible(showAdminModal.value, onboardingStore.isTourActive),
)
const hideManagerForever = ref(false)
const hideAdminForever = ref(false)

const resolvedByNni = ref<Record<string, ResolvedPerson>>({})
const resolveLoading = ref(false)

function isStorageDismissed(key: string): boolean {
  if (!import.meta.client) {
    return true
  }
  return localStorage.getItem(key) === '1'
}

function hasAdminResponsibilityForOrientation(
  roles: string[],
  habilitation: string | undefined
): boolean {
  if (isLegacyAdmin(roles)) {
    return true
  }
  return habilitation === ERole.ADMIN
}

const orientationPrimaryHabilitation = computed(function computeOrientationPrimaryHabilitation() {
  return derivePrimaryHabilitation(effectiveRoles.value)
})

const shouldShowAdminOrientationModal = computed(function computeAdminOrientationGate() {
  const status = orgAccessStatus.value
  if (!status?.adminWithoutDirectionScope) {
    return false
  }
  const rolesList = effectiveRoles.value
  return (
    !isDeveloper(rolesList) &&
    hasAdminResponsibilityForOrientation(rolesList, orientationPrimaryHabilitation.value)
  )
})

function collectNnisForResolve(status: OrgAccessStatusResponse): string[] {
  const nnis = new Set<string>()
  for (const row of status.directionAdminContactRefs) {
    nnis.add(row.nni)
  }
  const allAssignments = status.allDirectionAdminAssignments ?? status.directionAdminContactRefs
  for (const row of allAssignments) {
    nnis.add(row.nni)
  }
  for (const row of status.superAdminContactRefs) {
    nnis.add(row.nni)
  }
  return [...nnis]
}

async function loadResolvesForStatus(status: OrgAccessStatusResponse): Promise<void> {
  const allNnis = collectNnisForResolve(status)
  if (allNnis.length === 0) {
    resolvedByNni.value = {}
    return
  }
  resolveLoading.value = true
  const merged: Record<string, ResolvedPerson> = {}
  try {
    for (let start = 0; start < allNnis.length; start += DIRECTORY_RESOLVE_CHUNK) {
      const chunk = allNnis.slice(start, start + DIRECTORY_RESOLVE_CHUNK)
      if (chunk.length === 0) {
        continue
      }
      const body = await useApiMonOrga<{
        resolved: Array<{
          nni: string
          email: string | null
          givenName: string | null
          sn: string | null
        }>
      }>('POST', '/v1/directory/resolve-nnis', undefined, { nnis: chunk })
      for (const entry of body.resolved) {
        merged[entry.nni] = {
          email: entry.email,
          givenName: entry.givenName,
          sn: entry.sn,
        }
      }
    }
    resolvedByNni.value = merged
  } catch {
    resolvedByNni.value = {}
  } finally {
    resolveLoading.value = false
  }
}

watch(
  () => ({
    status: orgAccessStatus.value,
    adminGate: shouldShowAdminOrientationModal.value,
  }),
  async function onOrgAccessChange({ status, adminGate }) {
    if (!import.meta.client) {
      return
    }
    showManagerModal.value = false
    showAdminModal.value = false
    hideManagerForever.value = false
    hideAdminForever.value = false
    if (!status) {
      return
    }
    if (adminGate && !isStorageDismissed(STORAGE_ADMIN)) {
      showAdminModal.value = true
      await loadResolvesForStatus(status)
      return
    }
    if (status.managerUnplaced && !isStorageDismissed(STORAGE_MANAGER)) {
      showManagerModal.value = true
      await loadResolvesForStatus(status)
    }
  },
  { immediate: true }
)

watch(
  () => overrideState.value,
  async function onDevOidcProfilesOverrideChange() {
    if (!import.meta.dev || import.meta.server) {
      return
    }
    if (!loggedIn.value || !user.value?.accessToken) {
      return
    }
    await refreshOrgAccessStatus()
    await refreshMe()
  },
  { deep: true }
)

function resolvedForNni(nni: string): ResolvedPerson | undefined {
  return resolvedByNni.value[nni]
}

function closeManagerModal(): void {
  if (hideManagerForever.value) {
    localStorage.setItem(STORAGE_MANAGER, '1')
  }
  showManagerModal.value = false
}

function onManagerModalVisibilityChange(nextOpen: boolean): void {
  if (onboardingStore.isTourActive) {
    return
  }
  showManagerModal.value = nextOpen
}

function closeAdminModal(): void {
  if (hideAdminForever.value) {
    localStorage.setItem(STORAGE_ADMIN, '1')
  }
  showAdminModal.value = false
}

function onAdminModalVisibilityChange(nextOpen: boolean): void {
  if (onboardingStore.isTourActive) {
    return
  }
  showAdminModal.value = nextOpen
}

const adminDirectionList = computed(function adminDirectionRows() {
  const status = orgAccessStatus.value
  if (!status) {
    return [] as OrgAccessStatusResponse['directionAdminContactRefs']
  }
  return status.allDirectionAdminAssignments ?? status.directionAdminContactRefs
})

const managerDirectionAdminsGrouped = computed(function managerDirAdminsGrouped() {
  const status = orgAccessStatus.value
  if (!status) {
    return []
  }
  return groupDirectionAdminsByNni(status.directionAdminContactRefs)
})

const adminDirectionAdminsGrouped = computed(function adminDirAdminsGrouped() {
  return groupDirectionAdminsByNni(adminDirectionList.value)
})
</script>

<template>
  <ClientOnly>
    <AppNoticeModal
      :model-value="showManagerModalToUser"
      title="Aucun rattachement dans l’organigramme"
      confirm-label="Fermer"
      @update:model-value="onManagerModalVisibilityChange"
      @confirm="closeManagerModal"
    >
      <p>
        Vous êtes <strong>manager</strong>, mais aucune équipe ou section ne vous est rattachée
        dans Mon Orga. Tant que ce rattachement n’existe pas, vous ne pourrez pas agir sur l’organigramme comme
        prévu.
      </p>
      <p class="org-access-orientation__lead">
        Pour faire ajouter votre position, contactez un <strong>administrateur de direction</strong> (liste
        ci-dessous). En dernier recours, la <strong>super-administration</strong> peut aussi vous aiguiller.
      </p>
      <p v-if="resolveLoading" class="org-access-orientation__muted">Chargement des contacts…</p>
      <template v-else-if="orgAccessStatus">
        <p
          v-if="orgAccessStatus.superAdminContactRefs.length > 0"
          class="org-access-orientation__sub"
        >
          Super-administration
        </p>
        <ul
          v-if="orgAccessStatus.superAdminContactRefs.length > 0"
          class="org-access-orientation__list org-access-orientation__list--people"
        >
          <li
            v-for="row in orgAccessStatus.superAdminContactRefs"
            :key="`mgr-sup-${row.nni}`"
            class="org-access-orientation__person"
          >
            <div class="org-access-orientation__primary">{{ contactPrimaryLine(row.nni, resolvedForNni(row.nni)) }}</div>
            <div v-if="contactShowNniSecondary(row.nni, resolvedForNni(row.nni))" class="org-access-orientation__secondary">
              NNI {{ row.nni }}
            </div>
            <div v-if="contactMailtoAddress(row.nni, resolvedForNni(row.nni))" class="org-access-orientation__mail">
              <a :href="`mailto:${contactMailtoAddress(row.nni, resolvedForNni(row.nni))}`">{{
                contactMailtoAddress(row.nni, resolvedForNni(row.nni))
              }}</a>
            </div>
          </li>
        </ul>
        <p v-if="managerDirectionAdminsGrouped.length > 0" class="org-access-orientation__sub">
          Administrateurs de direction
        </p>
        <ul
          v-if="managerDirectionAdminsGrouped.length > 0"
          class="org-access-orientation__list org-access-orientation__list--people"
        >
          <li
            v-for="group in managerDirectionAdminsGrouped"
            :key="`mgr-adm-${group.nni}`"
            class="org-access-orientation__person"
          >
            <div class="org-access-orientation__primary">{{ contactPrimaryLine(group.nni, resolvedForNni(group.nni)) }}</div>
            <div v-if="contactShowNniSecondary(group.nni, resolvedForNni(group.nni))" class="org-access-orientation__secondary">
              NNI {{ group.nni }}
            </div>
            <div v-if="contactMailtoAddress(group.nni, resolvedForNni(group.nni))" class="org-access-orientation__mail">
              <a :href="`mailto:${contactMailtoAddress(group.nni, resolvedForNni(group.nni))}`">{{
                contactMailtoAddress(group.nni, resolvedForNni(group.nni))
              }}</a>
            </div>
            <p class="org-access-orientation__dir-scope">
              <span class="org-access-orientation__dir-scope-label">Directions :</span>
              {{ group.directionLabels.join(' · ') }}
            </p>
          </li>
        </ul>
        <p
          v-if="
            orgAccessStatus.superAdminContactRefs.length === 0 &&
              managerDirectionAdminsGrouped.length === 0
          "
          class="org-access-orientation__muted"
        >
          Aucun contact listé pour l’instant : passez par le support applicatif habituel.
        </p>
      </template>
      <SourceCheckbox
        v-model="hideManagerForever"
        size="medium"
        label="Ne plus afficher ce message sur cet appareil"
        class="org-access-orientation__dismiss"
      />
    </AppNoticeModal>

    <AppNoticeModal
      :model-value="showAdminModalToUser"
      title="Administrateur sans direction dans Mon Orga"
      confirm-label="Fermer"
      @update:model-value="onAdminModalVisibilityChange"
      @confirm="closeAdminModal"
    >
      <p>
        Vous avez un <strong>rôle administrateur</strong>, mais aucune direction ne vous est encore affectée dans
        Mon Orga. Sans cette affectation, vous ne pouvez pas modifier l’organigramme.
      </p>
      <p class="org-access-orientation__lead">
        <strong>Étape 1 :</strong> contactez d’abord la <strong>super-administration</strong> (liste ci-dessous).
        <strong>Étape 2 :</strong> si besoin, rapprochez-vous d’un <strong>administrateur de direction</strong> pour
        votre rattachement.
      </p>
      <p v-if="resolveLoading" class="org-access-orientation__muted">Chargement des contacts…</p>
      <template v-else-if="orgAccessStatus">
        <p v-if="orgAccessStatus.superAdminContactRefs.length > 0" class="org-access-orientation__sub">
          Super-administration
        </p>
        <ul
          v-if="orgAccessStatus.superAdminContactRefs.length > 0"
          class="org-access-orientation__list org-access-orientation__list--people"
        >
          <li
            v-for="row in orgAccessStatus.superAdminContactRefs"
            :key="`adm-sup-${row.nni}`"
            class="org-access-orientation__person"
          >
            <div class="org-access-orientation__primary">{{ contactPrimaryLine(row.nni, resolvedForNni(row.nni)) }}</div>
            <div v-if="contactShowNniSecondary(row.nni, resolvedForNni(row.nni))" class="org-access-orientation__secondary">
              NNI {{ row.nni }}
            </div>
            <div v-if="contactMailtoAddress(row.nni, resolvedForNni(row.nni))" class="org-access-orientation__mail">
              <a :href="`mailto:${contactMailtoAddress(row.nni, resolvedForNni(row.nni))}`">{{
                contactMailtoAddress(row.nni, resolvedForNni(row.nni))
              }}</a>
            </div>
          </li>
        </ul>
        <p v-if="adminDirectionAdminsGrouped.length > 0" class="org-access-orientation__sub">
          Administrateurs de direction (Mon Orga)
        </p>
        <ul
          v-if="adminDirectionAdminsGrouped.length > 0"
          class="org-access-orientation__list org-access-orientation__list--people"
        >
          <li
            v-for="group in adminDirectionAdminsGrouped"
            :key="`adm-dir-${group.nni}`"
            class="org-access-orientation__person"
          >
            <div class="org-access-orientation__primary">{{ contactPrimaryLine(group.nni, resolvedForNni(group.nni)) }}</div>
            <div v-if="contactShowNniSecondary(group.nni, resolvedForNni(group.nni))" class="org-access-orientation__secondary">
              NNI {{ group.nni }}
            </div>
            <div v-if="contactMailtoAddress(group.nni, resolvedForNni(group.nni))" class="org-access-orientation__mail">
              <a :href="`mailto:${contactMailtoAddress(group.nni, resolvedForNni(group.nni))}`">{{
                contactMailtoAddress(group.nni, resolvedForNni(group.nni))
              }}</a>
            </div>
            <p class="org-access-orientation__dir-scope">
              <span class="org-access-orientation__dir-scope-label">Directions :</span>
              {{ group.directionLabels.join(' · ') }}
            </p>
          </li>
        </ul>
      </template>
      <SourceCheckbox
        v-model="hideAdminForever"
        size="medium"
        label="Ne plus afficher ce message sur cet appareil"
        class="org-access-orientation__dismiss"
      />
    </AppNoticeModal>
  </ClientOnly>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/spacing" as *;

.org-access-orientation__muted {
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.org-access-orientation__lead {
  margin: 0 0 $spacing-m;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--color-text-secondary);
}

.org-access-orientation__sub {
  margin-top: $spacing-m;
  margin-bottom: $spacing-3xs;
  font-weight: 600;
}

.org-access-orientation__list {
  margin: 0;
  padding-left: 1.25rem;
}

.org-access-orientation__list--people {
  list-style: none;
  padding-left: 0;
}

.org-access-orientation__person {
  margin-bottom: $spacing-m;
  padding-bottom: $spacing-s;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.org-access-orientation__primary {
  font-weight: 600;
}

.org-access-orientation__secondary {
  margin-top: 2px;
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.org-access-orientation__mail {
  margin-top: $spacing-3xs;
  font-size: 0.9rem;
}

.org-access-orientation__dir {
  font-weight: 600;
  margin-bottom: $spacing-3xs;
}

.org-access-orientation__dir-scope {
  margin: $spacing-s 0 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--color-text-secondary);
}

.org-access-orientation__dir-scope-label {
  font-weight: 600;
  margin-right: 4px;
}

.org-access-orientation__dismiss {
  margin-top: $spacing-l;
}
</style>
